import type { PayrollEntry, Employee } from '../types';

export interface Form940Data {
  year: number;
  ein: string;
  businessName: string;
  businessAddress: string;

  // Part 2: Determine your FUTA tax before adjustments
  totalPayments: number;
  exemptPayments: number;
  paymentsExcludingFUTA: number;
  totalTaxableWages: number;

  // Part 3: Determine your adjustments
  adjustmentForFractions: number;
  adjustmentForSickPay: number;
  totalAdjustments: number;

  // Part 4: Determine your FUTA tax and balance due
  futaTaxBeforeAdjustments: number;
  futaTaxAfterAdjustments: number;

  // Part 5: Report your FUTA tax liability by quarter
  quarter1: number;
  quarter2: number;
  quarter3: number;
  quarter4: number;
  totalTaxLiability: number;

  // Additional details
  quarterlyBreakdown: {
    q1: { wages: number; tax: number };
    q2: { wages: number; tax: number };
    q3: { wages: number; tax: number };
    q4: { wages: number; tax: number };
  };
}

const FUTA_TAX_RATE = 0.006; // 0.6% after credit
const FUTA_WAGE_BASE = 7000; // First $7,000 per employee

export function calculateForm940Data(
  year: number,
  payrollEntries: PayrollEntry[],
  employees: Employee[],
  companyName: string,
  companyAddress: string,
  ein: string = '00-0000000'
): Form940Data {
  // Filter entries for the specified year
  const yearEntries = payrollEntries.filter(entry => {
    const entryDate = new Date(entry.payPeriod.start);
    return entryDate.getFullYear() === year;
  });

  // Track wages per employee for FUTA wage base limit
  const employeeWages = new Map<string, number>();
  const quarterlyData = {
    q1: { wages: 0, tax: 0 },
    q2: { wages: 0, tax: 0 },
    q3: { wages: 0, tax: 0 },
    q4: { wages: 0, tax: 0 },
  };

  let totalPayments = 0;
  let totalTaxableWages = 0;

  // Calculate wages and FUTA tax by quarter
  yearEntries.forEach(entry => {
    const entryDate = new Date(entry.payPeriod.start);
    const quarter = Math.floor(entryDate.getMonth() / 3) + 1;
    const quarterKey = `q${quarter}` as keyof typeof quarterlyData;

    // Get current employee wages for the year
    const currentEmployeeWages = employeeWages.get(entry.employeeId) || 0;

    // Calculate FUTA-taxable wages for this entry
    const remainingWageBase = Math.max(0, FUTA_WAGE_BASE - currentEmployeeWages);
    const futaTaxableWages = Math.min(entry.grossPay, remainingWageBase);

    // Update employee total wages
    employeeWages.set(entry.employeeId, currentEmployeeWages + entry.grossPay);

    // Update totals
    totalPayments += entry.grossPay;
    totalTaxableWages += futaTaxableWages;

    // Update quarterly data
    quarterlyData[quarterKey].wages += futaTaxableWages;
    quarterlyData[quarterKey].tax += futaTaxableWages * FUTA_TAX_RATE;
  });

  // Calculate FUTA tax
  const futaTaxBeforeAdjustments = totalTaxableWages * FUTA_TAX_RATE;

  // For simplicity, no adjustments in this version
  const adjustmentForFractions = 0;
  const adjustmentForSickPay = 0;
  const totalAdjustments = 0;

  const futaTaxAfterAdjustments = futaTaxBeforeAdjustments - totalAdjustments;

  return {
    year,
    ein,
    businessName: companyName,
    businessAddress: companyAddress,

    totalPayments,
    exemptPayments: 0,
    paymentsExcludingFUTA: 0,
    totalTaxableWages,

    adjustmentForFractions,
    adjustmentForSickPay,
    totalAdjustments,

    futaTaxBeforeAdjustments,
    futaTaxAfterAdjustments,

    quarter1: quarterlyData.q1.tax,
    quarter2: quarterlyData.q2.tax,
    quarter3: quarterlyData.q3.tax,
    quarter4: quarterlyData.q4.tax,
    totalTaxLiability: Object.values(quarterlyData).reduce((sum, q) => sum + q.tax, 0),

    quarterlyBreakdown: quarterlyData,
  };
}

export function formatCurrency(amount: number): string {
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function roundToTwoCents(amount: number): number {
  return Math.round(amount * 100) / 100;
}
